import { isSmallerThan, isLargerThan } from './comparators.esm.js';
import { RoundDirection } from './math.esm.js';

function _array_like_to_array(arr, len) {
    if (len == null || len > arr.length) len = arr.length;
    for(var i = 0, arr2 = new Array(len); i < len; i++)arr2[i] = arr[i];
    return arr2;
}
function _array_without_holes(arr) {
    if (Array.isArray(arr)) return _array_like_to_array(arr);
}
function _define_property(obj, key, value) {
    if (key in obj) {
        Object.defineProperty(obj, key, {
            value: value,
            enumerable: true,
            configurable: true,
            writable: true
        });
    } else {
        obj[key] = value;
    }
    return obj;
}
function _iterable_to_array(iter) {
    if (typeof Symbol !== "undefined" && iter[Symbol.iterator] != null || iter["@@iterator"] != null) return Array.from(iter);
}
function _non_iterable_spread() {
    throw new TypeError("Invalid attempt to spread non-iterable instance.\\nIn order to be iterable, non-array objects must have a [Symbol.iterator]() method.");
}
function _object_spread(target) {
    for(var i = 1; i < arguments.length; i++){
        var source = arguments[i] != null ? arguments[i] : {};
        var ownKeys = Object.keys(source);
        if (typeof Object.getOwnPropertySymbols === "function") {
            ownKeys = ownKeys.concat(Object.getOwnPropertySymbols(source).filter(function(sym) {
                return Object.getOwnPropertyDescriptor(source, sym).enumerable;
            }));
        }
        ownKeys.forEach(function(key) {
            _define_property(target, key, source[key]);
        });
    }
    return target;
}
function _to_consumable_array(arr) {
    return _array_without_holes(arr) || _iterable_to_array(arr) || _unsupported_iterable_to_array(arr) || _non_iterable_spread();
}
function _unsupported_iterable_to_array(o, minLen) {
    if (!o) return;
    if (typeof o === "string") return _array_like_to_array(o, minLen);
    var n = Object.prototype.toString.call(o).slice(8, -1);
    if (n === "Object" && o.constructor) n = o.constructor.name;
    if (n === "Map" || n === "Set") return Array.from(n);
    if (n === "Arguments" || /^(?:Ui|I)nt(?:8|16|32)(?:Clamped)?Array$/.test(n)) return _array_like_to_array(o, minLen);
}
// check if an array is defined and not empty - acts as a type guard
function isFilled(array) {
    return !isEmpty(array);
}
// checks if an array is empty or Nullish
function isEmpty(array) {
    return array == null || array.length === 0;
}
// returns the sum of the elements in a numeric array
function sum(array) {
    return array.reduce(function(result, num) {
        return result + num;
    }, 0);
}
// returns the element that has the best property value, e.g.:
//
//  bestBy([{ a: 1 }, { a: 2 }], 'a', -Number.Infinity, isLargerThan)
//
// returns the element that has the largest value in its `a` property
function bestBy(array, property, startingValue, comparator) {
    if (array.length === 0) {
        return null;
    }
    var matchingValue = startingValue;
    var matchingItem = null;
    array.forEach(function(item) {
        var propertyValue = item[property];
        if (comparator(propertyValue, matchingValue)) {
            matchingValue = propertyValue;
            matchingItem = item;
        }
    });
    return matchingItem;
}
// removes nullish values from an array
function compact(array) {
    return array.filter(function(val) {
        return val != null;
    });
}
// returns an array with the provided value (or the value itself if it's already an array)
function wrap(value) {
    return Array.isArray(value) ? value : [
        value
    ];
}
// returns the element which has the minimum value in the provided property argument
function minBy(array, property) {
    return bestBy(array, property, Number.POSITIVE_INFINITY, isSmallerThan);
}
// returns the element which has the maximum value in the provided property argument
function maxBy(array, property) {
    return bestBy(array, property, Number.NEGATIVE_INFINITY, isLargerThan);
}
function chunks(target, chunkSize) {
    return target.reduce(function(acc, item) {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        if (acc[acc.length - 1].length === chunkSize) {
            acc.push([]);
        }
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        acc[acc.length - 1].push(item);
        return acc;
    }, [
        []
    ]);
}
// puts each element in an array in a category defined by the provided `by` categorizing function, for example:
//
// categorize([1, 2, 3, 4, 5], (val) => val % 2)
// will return:
//
// { 0: [2, 4], 1: [1, 3, 5] }
function categorize(array, by) {
    return array.reduce(function(buckets, value) {
        var category = by(value);
        var bucketOfCategory = buckets[category];
        buckets[category] = bucketOfCategory == null ? [
            value
        ] : _to_consumable_array(bucketOfCategory).concat([
            value
        ]);
        return buckets;
    }, {});
}
// returns an array with its `n` last elements.
function takeLast(array, n) {
    return array.slice(Math.max(array.length - n, 0));
}
// flattens an array of object into a single object
function merge(array) {
    return array.reduce(function(result, object) {
        return _object_spread({}, result, object);
    }, {});
}
// checks if the first argument array is a subset of second argument array
function isSubset(possibleSubset, of) {
    return possibleSubset.every(function(value) {
        return of.includes(value);
    });
}
// search over an array of numbers and returns the index of the targetValue or -1 if its not found
// `returnClosestIndex` set to true will make the function return the index which value is the closest to the targetValue
// should perform at O(log n)
function searchIndex(list, targetValue) {
    var returnClosestIndex = arguments.length > 2 && arguments[2] !== void 0 ? arguments[2] : false, roundDirection = arguments.length > 3 ? arguments[3] : void 0;
    var start = 0;
    var end = list.length - 1;
    while(start <= end){
        var mid = Math.floor((start + end) / 2);
        var _list_mid;
        var midValue = (_list_mid = list[mid]) !== null && _list_mid !== void 0 ? _list_mid : Number.NaN;
        if (midValue === targetValue) {
            return mid;
        }
        if (midValue > targetValue) {
            end = mid - 1;
        } else {
            start = mid + 1;
        }
    }
    if (returnClosestIndex) {
        if (start === 0) {
            return 0;
        } else if (start > list.length - 1) {
            return end;
        }
        if (roundDirection === RoundDirection.Up) {
            return start;
        } else if (roundDirection === RoundDirection.Down) {
            return end;
        }
        var _list_start;
        var startValue = (_list_start = list[start]) !== null && _list_start !== void 0 ? _list_start : Number.NaN;
        var _list_end;
        var endValue = (_list_end = list[end]) !== null && _list_end !== void 0 ? _list_end : Number.NaN;
        var startDiff = Math.abs(targetValue - startValue);
        var endDiff = Math.abs(targetValue - endValue);
        return startDiff > endDiff ? end : start;
    }
    return -1;
}
function findOrFail(arr, predicate) {
    var found = arr.find(predicate);
    if (found === undefined) {
        throw new Error("Item not found");
    }
    return found;
}

export { bestBy, categorize, chunks, compact, findOrFail, isEmpty, isFilled, isSubset, maxBy, merge, minBy, searchIndex, sum, takeLast, wrap };
